import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { AuthTypes } from '@mono/proto-clients';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTenantAreas } from '../lib/access/useTenantAreas';
import { apiErrorMessage } from '../lib/api/apiErrorMessage';
import { authClient } from '../lib/connect/clients';
function isNotFound(err) {
    return err instanceof ConnectError && err.code === Code.NotFound;
}
export function SharePointOAuthSettings() {
    const [settings, setSettings] = useState({
        clientId: '',
        clientSecret: '',
        authority: 'https://login.microsoftonline.com/common',
        scope: 'https://graph.microsoft.com/.default',
        redirectUri: '',
        isEnabled: true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [loadError, setLoadError] = useState(null);
    // The stored secret comes back masked, never in clear (SAD-948): it only
    // tells us one exists, so the field may stay empty to keep it.
    const [hasStoredSecret, setHasStoredSecret] = useState(false);
    // GetSSOConfig and UpdateSSOConfig answer only admins, internal and IT
    // Admins (SAD-948), so asking as anyone else earns a guaranteed refusal —
    // and a silent, always-failing call is still a failing call.
    const canManage = useTenantAreas().canManageSignIn;
    // Load the stored settings. Re-run after a save so the card shows what was
    // actually persisted (e.g. that a secret is now stored).
    const loadSettings = useCallback(async ()=>{
        setLoadError(null);
        try {
            const { config } = await authClient.getSSOConfig({});
            if (config) {
                setHasStoredSecret(!!config.sharepointClientSecret);
                setSettings((prev)=>({
                        ...prev,
                        clientId: config.sharepointClientId || '',
                        authority: config.sharepointAuthority || prev.authority,
                        scope: config.sharepointScope || prev.scope,
                        redirectUri: config.sharepointRedirectUri || '',
                        isEnabled: config.sharepointEnabled,
                        // Never pre-fill client secret for security
                        clientSecret: ''
                    }));
            }
        } catch (err) {
            // Not configured yet is an empty form; anything else is a real failure
            // the admin must see, not an empty form they might save over.
            if (isNotFound(err)) {
                return;
            }
            setLoadError(apiErrorMessage(err, 'Failed to load the SharePoint OAuth settings'));
        }
    }, []);
    useEffect(()=>{
        if (canManage) {
            void loadSettings();
        }
    }, [
        canManage,
        loadSettings
    ]);
    const handleChange = (field, value)=>{
        setSettings((prev)=>({
                ...prev,
                [field]: value
            }));
        setError(null);
        setSuccess(false);
    };
    const handleSave = async ()=>{
        try {
            setLoading(true);
            setError(null);
            setSuccess(false);
            // Validation the express route performed before it would call mariejois.
            // It has to live here now: UpdateSSOConfig takes the config as given.
            const problems = [];
            if (!settings.clientId.trim()) problems.push('Client ID is required');
            if (!settings.clientSecret.trim() && !hasStoredSecret) {
                problems.push('Client Secret is required');
            }
            if (!settings.authority.trim()) problems.push('Authority is required');
            else if (!settings.authority.startsWith('https://')) {
                problems.push('Authority must be an HTTPS URL');
            }
            if (settings.redirectUri && !settings.redirectUri.startsWith('http')) {
                problems.push('Redirect URI must be a valid URL');
            }
            if (problems.length > 0) {
                throw new Error(problems.join('; '));
            }
            // Read-merge-write, exactly as the express handler did. UpdateSSOConfig
            // replaces the whole SSOConfig, so sending only the SharePoint fields
            // would silently clear this tenant's Google and SAML configuration.
            // A tenant with no SSO row yet gets one created holding only the
            // SharePoint fields: every other provider stays off, and email/password
            // stays on — exactly what "no row" means at sign-in (GetProviders).
            const existing = await authClient.getSSOConfig({}).then((r)=>r.config, (err)=>{
                if (isNotFound(err)) return new AuthTypes.SSOConfig({
                    emailPasswordEnabled: true
                });
                throw err;
            });
            if (!existing) {
                throw new Error('Could not read the current SSO configuration');
            }
            await authClient.updateSSOConfig({
                config: {
                    ...existing,
                    sharepointEnabled: settings.isEnabled,
                    sharepointClientId: settings.clientId.trim(),
                    // Empty keeps the stored secret: mariejois ignores an empty or
                    // masked value on update.
                    sharepointClientSecret: settings.clientSecret.trim(),
                    sharepointAuthority: settings.authority.trim(),
                    sharepointScope: settings.scope.trim() || 'https://graph.microsoft.com/.default',
                    sharepointRedirectUri: settings.redirectUri.trim()
                }
            });
            await loadSettings();
            setSuccess(true);
            setTimeout(()=>setSuccess(false), 3000);
        } catch (err) {
            setError(apiErrorMessage(err, 'Failed to save settings'));
        } finally{
            setLoading(false);
        }
    };
    const isValid = !loadError && settings.clientId.trim() && (settings.clientSecret.trim() || hasStoredSecret) && settings.authority.trim() && settings.redirectUri.trim();
    // Tenant sign-in configuration is for admins, internal and IT Admins
    // (SAD-947/948); members keep their own Connect button in the section
    // around this card.
    if (!canManage) {
        return null;
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "max-w-2xl mx-auto p-6",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "bg-card rounded-lg shadow-sm border border-border",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "px-6 py-4 border-b border-border",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-lg font-semibold text-foreground",
                            children: "SharePoint OAuth Configuration"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground mt-1",
                            children: "Configure Microsoft OAuth credentials to enable SharePoint document access"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "p-6 space-y-4",
                    children: [
                        loadError && /*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-3 p-4 bg-danger-muted border border-danger rounded-lg",
                            role: "alert",
                            children: [
                                /*#__PURE__*/ _jsx(AlertCircle, {
                                    className: "w-5 h-5 text-danger flex-shrink-0 mt-0.5"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "font-medium text-danger",
                                            children: "Could not load the current settings"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-danger mt-1",
                                            children: loadError
                                        })
                                    ]
                                })
                            ]
                        }),
                        error && /*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-3 p-4 bg-danger-muted border border-danger rounded-lg",
                            children: [
                                /*#__PURE__*/ _jsx(AlertCircle, {
                                    className: "w-5 h-5 text-danger flex-shrink-0 mt-0.5"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "font-medium text-danger",
                                            children: "Configuration Error"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-danger mt-1",
                                            children: error
                                        })
                                    ]
                                })
                            ]
                        }),
                        success && /*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-3 p-4 bg-success-muted border border-success rounded-lg",
                            children: [
                                /*#__PURE__*/ _jsx(CheckCircle, {
                                    className: "w-5 h-5 text-success flex-shrink-0 mt-0.5"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "font-medium text-success",
                                            children: "Settings Saved"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-success mt-1",
                                            children: "SharePoint OAuth configuration has been updated successfully"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-4",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-foreground mb-1",
                                            children: "Client ID"
                                        }),
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "text",
                                            value: settings.clientId,
                                            onChange: (e)=>handleChange('clientId', e.target.value),
                                            placeholder: "e.g., 550e8400-e29b-41d4-a716-446655440000",
                                            className: "w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground mt-1",
                                            children: "Application (client) ID from Azure AD app registration"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-foreground mb-1",
                                            children: "Client Secret"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "relative",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: showSecret ? 'text' : 'password',
                                                    value: settings.clientSecret,
                                                    onChange: (e)=>handleChange('clientSecret', e.target.value),
                                                    placeholder: hasStoredSecret ? 'Stored — leave empty to keep it' : '••••••••••••••••••••',
                                                    className: "w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                                                }),
                                                /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    onClick: ()=>setShowSecret(!showSecret),
                                                    className: "absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground hover:text-foreground",
                                                    children: showSecret ? 'Hide' : 'Show'
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground mt-1",
                                            children: "Client secret from Azure AD app registration"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-foreground mb-1",
                                            children: "Authority"
                                        }),
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "text",
                                            value: settings.authority,
                                            onChange: (e)=>handleChange('authority', e.target.value),
                                            placeholder: "https://login.microsoftonline.com/common",
                                            className: "w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground mt-1",
                                            children: "Microsoft authentication endpoint"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-foreground mb-1",
                                            children: "Scope"
                                        }),
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "text",
                                            value: settings.scope,
                                            onChange: (e)=>handleChange('scope', e.target.value),
                                            placeholder: "https://graph.microsoft.com/.default",
                                            className: "w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground mt-1",
                                            children: "OAuth scope for Microsoft Graph API access"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-foreground mb-1",
                                            children: "Redirect URI"
                                        }),
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "text",
                                            value: settings.redirectUri,
                                            onChange: (e)=>handleChange('redirectUri', e.target.value),
                                            placeholder: "https://your-domain.com/api/sharepoint/oauth/callback",
                                            className: "w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground mt-1",
                                            children: "Must match the redirect URI configured in Azure AD"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "checkbox",
                                            id: "isEnabled",
                                            checked: settings.isEnabled,
                                            onChange: (e)=>handleChange('isEnabled', e.target.checked),
                                            className: "w-4 h-4 border-input rounded text-info focus:ring-ring"
                                        }),
                                        /*#__PURE__*/ _jsx("label", {
                                            htmlFor: "isEnabled",
                                            className: "text-sm font-medium text-foreground",
                                            children: "Enable SharePoint OAuth"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-6 p-4 bg-info-muted border border-info rounded-lg",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-sm font-medium text-info mb-2",
                                    children: "Setup Instructions"
                                }),
                                /*#__PURE__*/ _jsxs("ol", {
                                    className: "text-xs text-info space-y-1 list-decimal list-inside",
                                    children: [
                                        /*#__PURE__*/ _jsx("li", {
                                            children: "Go to Azure Portal → App registrations"
                                        }),
                                        /*#__PURE__*/ _jsx("li", {
                                            children: "Create a new application registration"
                                        }),
                                        /*#__PURE__*/ _jsx("li", {
                                            children: "Copy the Client ID and Client Secret"
                                        }),
                                        /*#__PURE__*/ _jsx("li", {
                                            children: "Set the Redirect URI in Azure to match the value above"
                                        }),
                                        /*#__PURE__*/ _jsx("li", {
                                            children: "Grant Microsoft Graph API permissions (Sites.Read.All, Files.Read.All)"
                                        }),
                                        /*#__PURE__*/ _jsx("li", {
                                            children: "Fill in all fields and click Save"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex justify-end gap-2 px-6 py-4 border-t border-border bg-muted",
                    children: /*#__PURE__*/ _jsxs("button", {
                        onClick: handleSave,
                        disabled: loading || !isValid,
                        className: "inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
                        children: [
                            loading && /*#__PURE__*/ _jsx(Loader, {
                                className: "w-4 h-4 animate-spin"
                            }),
                            loading ? 'Saving...' : 'Save Configuration'
                        ]
                    })
                })
            ]
        })
    });
}
